import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output, inject } from '@angular/core';

import type { BillingPlan } from '@core/billing-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import { SelectComponent } from '@core/user-interface-legacy';

import { findNextIntDivisibleBy } from '../../helpers/find-next-divisible';

@Component({
  selector: 'rt-job-slots-picker',
  templateUrl: './job-slots-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectComponent, TranslatePipe],
})
export class JobSlotsPickerComponent implements OnChanges {
  public static readonly CustomProductSlotsLimit = 100;
  public static readonly DefaultProductSlotsLimit = 50;
  public static readonly MaxSlotsToBuy = 1000;
  public static readonly MinSlotsToBuy = 10;

  private readonly i18n = inject(I18nService);

  @Input() public optionStepBy: number = 5;
  @Input()
  public set value(inputValue: number) {
    this._value = Math.max(JobSlotsPickerComponent.MinSlotsToBuy, inputValue);

    if (this._value !== inputValue) {
      this.slotsChange.emit(this._value);
    }
  }
  public get value() {
    return this._value;
  }
  @Input() public product: BillingPlan;
  @Input() public addContactWithSalesOption: boolean;
  @Input() public ownedSlots: number;
  @Input() public isManualSubscription: boolean;

  public slotsChange = output<number>();

  public options: SelectOption[];

  private _value: number;

  public ngOnChanges(changes: SimpleChanges): void {
    const { optionStepBy } = this;
    const min = this.getMinSlots();
    const limit = Math.min(
      this.product.custom
        ? this.product.baseSlots + JobSlotsPickerComponent.CustomProductSlotsLimit
        : this.ownedSlots + JobSlotsPickerComponent.DefaultProductSlotsLimit,
      JobSlotsPickerComponent.MaxSlotsToBuy,
    );

    const max = Math.min(
      Math.max(limit, Number.isFinite(this.ownedSlots) ? this.ownedSlots + this.optionStepBy : 0),
      JobSlotsPickerComponent.MaxSlotsToBuy,
    );

    this.options = this.generateSlotOptions(min, max, optionStepBy);

    if (this.addContactWithSalesOption) {
      this.options = [
        ...this.options,
        {
          value: Number.POSITIVE_INFINITY,
          label: this.getSlotLabel(
            `${
              max % this.optionStepBy === 0 ? max : findNextIntDivisibleBy(max, this.optionStepBy)
            }+`,
          ),
        },
      ];
    }

    this.options = this.options.sort((a, b) => <number>a.value - <number>b.value);
  }

  private generateSlotOptions(min: number, max: number, step: number): SelectOption[] {
    return new Array(Math.floor(max / step))
      .fill(null)
      .map((_, i) => (i + 1) * step)
      .filter(value => value >= min)
      .map(value => this.getSlotOption(value));
  }

  private getSlotOption(value: number): SelectOption {
    return {
      label: this.getSlotLabel(value),
      value,
    };
  }

  private getSlotLabel(value: number | string) {
    return this.i18n.translate('app.pricing.job_slots_count', { count: value });
  }

  private getMinSlots() {
    return Math.max(this.ownedSlots, JobSlotsPickerComponent.MinSlotsToBuy);
  }
}
